import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { bindTomSelectClipboardSync } from "../legacy/search";

const treeCache = new Map();
const RETRY_DELAY_MS = 1000;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function responseContentType(response) {
  return response.headers && typeof response.headers.get === "function"
    ? response.headers.get("content-type") || ""
    : "";
}

async function fetchTree(url) {
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Set tree request failed: ${response.status}`);

  const contentType = responseContentType(response);
  if (contentType && !contentType.toLowerCase().includes("application/json")) {
    throw new Error(`Set tree returned non-JSON content: ${contentType}`);
  }

  return response.json();
}

function loadTree(url, retry = true) {
  if (!treeCache.has(url)) {
    const request = fetchTree(url)
      .catch((error) => {
        if (!retry) throw error;
        return delay(RETRY_DELAY_MS).then(() => fetchTree(url));
      })
      .catch((error) => {
        treeCache.delete(url);
        throw error;
      });
    treeCache.set(url, request);
  }
  return treeCache.get(url);
}

export default class extends Controller {
  static values = {
    url: String,
    valueField: { type: String, default: "code" },
    multiselect: { type: Boolean, default: false },
    rootsOnly: { type: Boolean, default: false },
  };

  connect() {
    this.connectionId = (this.connectionId || 0) + 1;
    const connectionId = this.connectionId;

    loadTree(this.urlValue).then((tree) => {
      if (connectionId !== this.connectionId || !this.element.isConnected) return;

      const selectedOptions = this.selectedOptionsFromNativeSelect();
      const initialValues = selectedOptions.map((option) => option.value);
      const { options, byCode, childrenOf } = this.buildFlatOptions(tree);
      const mergedOptions = this.mergeSelectedOptions(options, selectedOptions);
      this.clearLoadError();
      this.initTomSelect(mergedOptions, byCode, childrenOf, initialValues);
      this.dispatch("ready", { bubbles: true });
    }).catch(() => {
      if (connectionId === this.connectionId && this.element.isConnected) {
        this.showLoadError();
      }
    });
  }

  disconnect() {
    this.connectionId = (this.connectionId || 0) + 1;
    if (this.tomSelect) {
      const selection = this.selectionFromTomSelect();
      this.tomSelect.destroy();
      this.restoreNativeSelection(selection);
      this.tomSelect = null;
    }
  }

  selectedOptionsFromNativeSelect() {
    return Array.from(this.element.selectedOptions)
      .filter((option) => option.value !== "")
      .map((option) => ({
        value: option.value,
        label: option.textContent.trim() || option.value,
        code: option.dataset.code || option.value,
      }));
  }

  selectionFromTomSelect() {
    const rawValue = this.tomSelect.getValue();
    const values = Array.isArray(rawValue) ? rawValue : [rawValue].filter((value) => value !== "");

    return values.map((value) => {
      const option = this.tomSelect.options[value] || {};
      return {
        value: String(value),
        label: this.optionLabel(option, value),
        code: String(option.code || value),
      };
    });
  }

  restoreNativeSelection(selection) {
    const existingOptions = new Map(
      Array.from(this.element.options).map((option) => [option.value, option])
    );

    Array.from(this.element.options).forEach((option) => {
      option.selected = false;
    });

    if (!selection || selection.length === 0) {
      this.element.selectedIndex = -1;
      return;
    }

    (selection || []).forEach((selected) => {
      let option = existingOptions.get(selected.value);
      if (!option) {
        option = new Option(selected.label, selected.value);
        this.element.add(option);
      }
      option.textContent = selected.label;
      option.dataset.code = selected.code;
      option.selected = true;
    });
  }

  showLoadError() {
    this.element.dataset.setTreeSelectError = "true";
    this.notifyError(new Error("Set tree failed to load"));

    if (this.errorElement && this.errorElement.isConnected) return;

    const error = document.createElement("div");
    error.className = "form-text text-danger set-tree-select-error";
    error.setAttribute("role", "alert");
    error.textContent = "Set list could not be loaded. Existing selected values remain available.";
    this.element.insertAdjacentElement("afterend", error);
    this.errorElement = error;
  }

  clearLoadError() {
    delete this.element.dataset.setTreeSelectError;
    if (this.errorElement && this.errorElement.isConnected) {
      this.errorElement.remove();
    }
    this.errorElement = null;
  }

  notifyError(error) {
    if (window.Bugsnag && typeof window.Bugsnag.notify === "function") {
      window.Bugsnag.notify(error);
    }
  }

  safeText(value, fallback = "") {
    const text = value === undefined || value === null ? "" : String(value).trim();
    return text || fallback;
  }

  // Flatten the nested tree into a list of options, tracking depth and parent relationships.
  buildFlatOptions(tree) {
    const options = [];
    const byCode = {};
    const childrenOf = {};

    const walk = (nodes, depth, parentCode, rootCode) => {
      nodes.forEach((node) => {
        const valueFallback = this.safeText(node.id, this.safeText(node.code, this.safeText(node.name, "")));
        const code = this.safeText(node.code, valueFallback);
        const name = this.safeText(node.name, code || valueFallback);
        const myRootCode = depth === 0 ? code : rootCode;
        const opt = {
          id: node.id === undefined || node.id === null ? undefined : String(node.id),
          code,
          name,
          image_symbol_svg_url: node.image_symbol_svg_url,
          depth,
          parentCode,
          rootCode: myRootCode,
        };
        options.push(opt);
        byCode[code] = opt;
        childrenOf[code] = childrenOf[code] || [];
        if (parentCode) {
          childrenOf[parentCode] = childrenOf[parentCode] || [];
          childrenOf[parentCode].push(code);
        }
        if (node.children && node.children.length) {
          walk(node.children, depth + 1, code, myRootCode);
        }
      });
    };

    walk(tree, 0, null, null);
    return { options, byCode, childrenOf };
  }

  mergeSelectedOptions(options, selectedOptions) {
    const existingValues = new Set(options.map((option) => this.optionValue(option)));
    const selectedFallbacks = selectedOptions
      .filter((option) => option.value !== "" && !existingValues.has(option.value))
      .map((option) => {
        const label = option.label || option.value;
        return {
          id: this.valueFieldValue === "id" ? option.value : undefined,
          code: this.valueFieldValue === "code" ? option.value : option.code || option.value,
          name: label,
          depth: 0,
          parentCode: null,
          rootCode: option.code || option.value,
          fallback: true,
        };
      });

    return options.concat(selectedFallbacks);
  }

  optionValue(option) {
    return String(option[this.valueFieldValue] ?? "");
  }

  optionLabel(option, value) {
    if (!option || Object.keys(option).length === 0) return String(value);
    const code = this.safeText(option.code, String(value));
    const name = this.safeText(option.name, code);
    return code && code !== name ? `${name} (${code})` : name;
  }

  // Return all descendant codes (children + grandchildren) for a given code.
  allDescendants(code, childrenOf) {
    const result = [];
    const queue = [...(childrenOf[code] || [])];
    while (queue.length) {
      const c = queue.shift();
      result.push(c);
      (childrenOf[c] || []).forEach((child) => queue.push(child));
    }
    return result;
  }

  // Compute which option codes should be visible for a given query.
  // Returns null to show all (empty query), or a Set of visible codes.
  computeVisible(query, options, byCode, childrenOf) {
    if (!query || query.trim() === "") return null;

    const q = query.toLowerCase();
    const visible = new Set();

    options.forEach((opt) => {
      const matches =
        this.safeText(opt.name).toLowerCase().includes(q) ||
        this.safeText(opt.code).toLowerCase().includes(q);

      if (!matches) return;

      if (opt.depth === 0) {
        // Root match: show it and all descendants
        visible.add(opt.code);
        this.allDescendants(opt.code, childrenOf).forEach((c) =>
          visible.add(c)
        );
      } else {
        // Child/grandchild match: show it and its full ancestor chain
        visible.add(opt.code);
        let cur = opt;
        while (cur.parentCode) {
          visible.add(cur.parentCode);
          cur = byCode[cur.parentCode];
        }
      }
    });

    return visible;
  }

  renderSetEntry(item, escape) {
    const code = String(item.code || item.name || this.optionValue(item)).toLowerCase();
    const labelCode = String(item.code || this.optionValue(item));
    const unsupported = window.__keyruneUnsupportedSvgMap || {};
    let icon;
    if (unsupported[code]) {
      icon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(unsupported[code])}); -webkit-mask-image: url(${escape(unsupported[code])});" title="${escape(item.name)}"></span>`;
    } else if (item.image_symbol_svg_url && unsupported[code] !== undefined) {
      icon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(item.image_symbol_svg_url)}); -webkit-mask-image: url(${escape(item.image_symbol_svg_url)});" title="${escape(item.name)}"></span>`;
    } else {
      icon = `<i class="ss ss-${escape(code)}"></i>`;
    }
    return `<span style="display:inline-flex; align-items:center; gap:6px;"><span style="display:inline-flex; align-items:center;">${icon}</span><span>${escape(item.name)}</span><span class="text-muted">${escape(labelCode)}</span></span>`;
  }

  initTomSelect(options, byCode, childrenOf, initialValues) {
    if (this.tomSelect) return;

    const self = this;
    const plugins = this.multiselectValue ? ["remove_button"] : ["clear_button"];

    const filteredOptions = this.rootsOnlyValue
      ? options.filter((o) => o.depth === 0)
      : options;

    this.tomSelect = new TomSelect(this.element, {
      options: filteredOptions,
      valueField: this.valueFieldValue,
      labelField: "name",
      searchField: ["name", "code"],
      maxItems: this.multiselectValue ? null : 1,
      maxOptions: 2000,
      items: initialValues,
      plugins,
      placeholder: "Select a set...",

      score: function (search) {
        if (self.rootsOnlyValue) {
          // Only root sets; use simple substring match
          const q = search.toLowerCase();
          return function (item) {
            if (!q) return 1;
            return self.safeText(item.name).toLowerCase().includes(q) ||
              self.safeText(item.code).toLowerCase().includes(q)
              ? 1
              : 0;
          };
        }

        const visible = self.computeVisible(
          search,
          filteredOptions,
          byCode,
          childrenOf
        );
        return function (item) {
          if (visible === null) return 1;
          return visible.has(item.code) ? 1 : 0;
        };
      },

      render: {
        option: function (item, escape) {
          const prefix = item.depth > 0 ? "↳ " : "";
          return `<div class="set-tree-option depth-${item.depth}" style="padding-left:${8 + item.depth * 14}px">${prefix}${self.renderSetEntry(item, escape)}</div>`;
        },
        item: function (item, escape) {
          return `<div>${self.renderSetEntry(item, escape)}</div>`;
        },
      },
    });
    // bootstrap_form adds form-select to the <select>; TomSelect copies it to
    // ts-wrapper which causes a double border — remove it.
    this.tomSelect.wrapper.classList.remove("form-select");
    bindTomSelectClipboardSync(this.element);
  }
}
